import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslate } from "../i18n/useTranslate";
import { usePreferences } from "../context/PreferencesContext";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  PieChart, Pie, Cell, ResponsiveContainer, ComposedChart,
  Tooltip, Legend, XAxis, YAxis, CartesianGrid
} from "recharts";

import ChartCard from "../components/ui/ChartCard";
import ChartTooltip from "../components/ui/ChartTooltip";
import HeroPanel from "../components/ui/HeroPanel";
import MetricCard from "../components/ui/MetricCard";
import GaugeCard from "../components/ui/GaugeCard";
import MapPlaceholder from "../components/ui/MapPlaceholder";
import SectionHeader from "../components/ui/SectionHeader";
import IncidentFeed from "../components/ui/IncidentFeed";
import DepartmentCard from "../components/ui/DepartmentCard";

import {
  kpiData, alertFeed, crowdTrendData, aiAssuranceData,
  routeSaturation, departmentStatus, serviceStress,
  emergencyUnits, alertSeverityBreakdown, topStats,
  resourceDeployment, commandTimeline
} from "../data/mockData";
import { googleColor } from "../data/googleColors";
import { TICKER, TICKER_INTERVAL_MS, LIVE_OPS_LABEL, FEED_LABEL } from "../data/liveFeed";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { STATUS, statusHex, statusClasses, thresholdStatus } from "../data/statusColors";

import {
  Shield, Activity, Users, Zap, TrendingUp, TrendingDown,
  CheckCircle, AlertTriangle, Clock, ChevronRight,
  AlertCircle, Brain, Layers, Radio, MapPin, BadgeCheck, Bot,
  BarChart2
} from "lucide-react";

// Zone/service load status uses a 4-tier critical/serious/warning/good
// vocabulary (distinct from RiskCard's own critical/warning/info/good
// scheme) — kept local since it's specific to this page's two "load %"
// widgets below, both fed by the same critical/high/moderate/else shape.
const loadBucket = (status) =>
  status === "critical" ? "critical" :
  status === "high"     ? "serious"  :
  status === "moderate" ? "warning"  : "good";

const timelineIconStyle = {
  escalation: { bg: STATUS.critical.bg, text: STATUS.critical.text, Icon: AlertTriangle },
  alert:      { bg: STATUS.warning.bg,  text: STATUS.warning.text,  Icon: AlertCircle },
  action:     { bg: STATUS.info.bg,     text: STATUS.info.text,     Icon: CheckCircle },
  missing:    { bg: STATUS.serious.bg,  text: STATUS.serious.text,  Icon: Users },
  ai:         { bg: "bg-violet-100",    text: "text-violet-700",    Icon: Brain }, // no violet bucket in statusColors
  resolved:   { bg: STATUS.good.bg,     text: STATUS.good.text,     Icon: CheckCircle },
  advisory:   { bg: "bg-sky-100",       text: "text-sky-700",       Icon: Radio }, // no sky bucket in statusColors
};

const ALERT_TYPE_BUCKET = { danger: "critical", warning: "warning", success: "good" };
const ALERT_PRIORITY_BUCKET = { HIGH: "critical", MED: "warning" };
const ALERT_STATUS_BUCKET = { Active: "critical", Escalated: "critical", Resolved: "good", Closed: "good" };

// Hero module tags link straight through to the page that owns each
// capability, so the landing summary doubles as a navigation shortcut.
const MODULE_TAG_ROUTE = {
  "AI Stack Assurance":          "/ai-assurance",
  "Crowd Risk Digital Twin":     "/crowd-intelligence",
  "Field Officer Copilot":       "/field-ops",
  "Pilgrim Safety Intelligence": "/pilgrim-support",
  "Inter-Agency War Room":       "/command",
  "Service Stress Prediction":   "/services",
  "DPDP Governance Layer":       "/governance",
};

export default function DashboardHome() {
  const [activeSection, setActiveSection] = useState("overview");
  const t = useTranslate();
  const { lang } = usePreferences();

  // Ticks once a minute, not once a second: this shows a date, so a per-second
  // interval would re-render the whole dashboard 60x more often for nothing.
  const [today, setToday] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setToday(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  // "-u-nu-latn" pins Latin digits in the mr-IN locale, whose CLDR default is
  // Devanagari numerals — the same rule the header clock follows, so the date
  // reads consistently with every other figure on the page.
  /* Rotating live feed, moved here from the header bar. */
  const [tickerIdx, setTickerIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setTickerIdx((i) => (i + 1) % TICKER.length),
      TICKER_INTERVAL_MS
    );
    return () => clearInterval(id);
  }, []);

  const todayLabel = today.toLocaleDateString(
    lang === "mr" ? "mr-IN-u-nu-latn" : "en-IN",
    { weekday: "short", day: "numeric", month: "short", year: "numeric" }
  );

  return (
    <div className="space-y-8">

      {/* ── HERO COMMAND BANNER ── */}
      <HeroPanel photo="/images/command-centre.jpg">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-saffron-light rounded-2xl flex items-center justify-center border border-saffron/30">
                <Shield size={22} className="text-saffron-dark" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-black leading-none tracking-tight text-slate-900">{t("Kumbh AI")}</h1>
                <div className="text-slate-500 text-sm font-semibold mt-0.5">
                  {t("Simhastha AI Command Assurance Platform")}
                </div>
              </div>
            </div>

            <p className="text-slate-600 text-sm max-w-2xl leading-relaxed mb-4">
              <span className="font-black text-slate-900">{t("Not replacing the Kumbh AI Stack.")}</span>{" "}
              {t("Validating, orchestrating and creating trusted, auditable governance for Simhastha-scale pilgrimage safety for CM Office, CS, NTKMA, Police, Health and all field commanders.")}
            </p>

            <div className="flex flex-wrap gap-2">
              {["AI Stack Assurance","Crowd Risk Digital Twin","Field Officer Copilot",
                "Pilgrim Safety Intelligence","Inter-Agency War Room","Service Stress Prediction",
                "DPDP Governance Layer"].map(tag => (
                <Link key={tag} to={MODULE_TAG_ROUTE[tag]}
                  className="text-[10px] font-bold bg-saffron-light text-saffron-dark px-2.5 py-1 rounded-full border border-saffron/30 hover:bg-saffron/20 transition-colors">
                  {t(tag)}
                </Link>
              ))}
            </div>

            {/* Live operations strip — relocated from the header bar, where it
                competed with the wordmark for the same sliver of space. Here
                it sits beside the figures it describes and has room to be
                read. */}
            <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-600 live-dot" />
                <span className="text-[10px] font-black text-emerald-800">
                  {LIVE_OPS_LABEL[lang] ?? LIVE_OPS_LABEL.en}
                </span>
              </span>

              <span className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-slate-600">
                <Radio size={9} />
                {FEED_LABEL[lang] ?? FEED_LABEL.en}
              </span>

              <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-slate-700">
                {TICKER[tickerIdx][lang] ?? TICKER[tickerIdx].en}
              </span>

              {/* Kumbh Sathi lives here now rather than in the nav — it is a
                  thing you launch from the overview, not a section you browse. */}
              <Link
                to="/sahayak"
                className="inline-flex items-center gap-1.5 rounded-lg bg-saffron px-2.5 py-1 text-[11px] font-black text-white hover:brightness-95 transition-all flex-shrink-0"
              >
                <Bot size={12} />
                {lang === "mr" ? "कुंभ साथी" : "Kumbh Sathi"}
              </Link>
            </div>
          </div>

          <div className="flex flex-col gap-3 flex-shrink-0">
            {(() => {
              const c0 = googleColor(0);
              return (
                <div className="rounded-2xl p-3 text-center min-w-[140px]" style={{ background:c0.bgLight, border:`1px solid ${c0.border}` }}>
                  <div className="flex items-center justify-center gap-1.5 mb-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 live-dot" />
                    <span className="text-[10px] font-black" style={{ color:c0.text }}>{t("ALL SYSTEMS LIVE")}</span>
                  </div>
                  <div className="text-2xl font-black" style={{ color:c0.text }}>6,84,210</div>
                  <div className="text-xs" style={{ color:c0.text }}>{t("Active Pilgrims")}</div>
                </div>
              );
            })()}
            <div className="grid grid-cols-2 gap-2">
              {[
                { val:`${topStats.openCases}`,          label:"Open Incidents" },
                { val:`${aiAssuranceData.accuracy}%`,   label:"AI Confidence"  },
              ].map((s, idx) => {
                const c = googleColor(idx + 1);
                return (
                  <div key={s.label} className="rounded-xl p-2 text-center" style={{ background:c.bgLight, border:`1px solid ${c.border}` }}>
                    <div className="text-lg font-black" style={{ color:c.text }}>{s.val}</div>
                    <div className="text-[9px]" style={{ color:c.text }}>{t(s.label)}</div>
                  </div>
                );
              })}
            </div>
            {/* Today's date. Moved here from the header bar: on the white
                panel it has room to be read, rather than competing with the
                clock in a crowded saffron strip. */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-center">
              <div className="text-[9px] font-black uppercase tracking-wider text-slate-400">
                {t("Today")}
              </div>
              <div className="text-sm font-bold text-slate-700 mt-0.5">{todayLabel}</div>
            </div>

            <div className="text-center text-xs text-slate-500">
              <div className="font-bold">{t("Simhastha Kumbh 2027")}</div>
              <div>{t("Nashik · Trimbakeshwar, Maharashtra")}</div>
            </div>
          </div>
        </div>
      </HeroPanel>

      {/* ── EXECUTIVE KPIs ── */}
      <section>
        <SectionHeader icon="Activity" title="Executive Command KPIs"
          subtitle="Real-time platform-wide metrics · Simhastha Kumbh 2027 · Nashik & Trimbakeshwar, Maharashtra"
          badge="LIVE"
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {kpiData.map((card, i) => (
            <MetricCard key={card.id} title={card.title} value={card.value}
              icon={card.icon} trend={card.trend} index={i} live
            />
          ))}
        </div>
      </section>

      {/* ── COMMAND MAP + ALERT FEED ── */}
      <section>
        <SectionHeader icon="Map" title="Operational Command Map"
          subtitle="Live crowd density, zone risk levels, incident overlays · Nashik city GIS view"
          action={
            <div className="flex items-center gap-2">
              <button className="btn-secondary text-xs">
                <Layers size={12} /> {t("All Zones")}
              </button>
              <button className="btn-primary text-xs">
                <MapPin size={12} /> {t("Full Screen")}
              </button>
            </div>
          }
        />
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2">
            <MapPlaceholder height={420} showHeatmap={true} />
          </div>

          {/* Live alert feed */}
          <div className="card p-4 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-black text-slate-900">{t("Live Incident Feed")}</h3>
              <span className="flex items-center gap-1 text-[9px] font-black text-emerald-600">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />{t("LIVE")}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto max-h-80">
              <IncidentFeed alerts={alertFeed} limit={alertFeed.length} />
            </div>
            <p className="text-[9px] text-slate-400 mt-2">
              {t("Showing")} {alertFeed.length} {t("most recent of")} {topStats.totalAlerts} {t("alerts today")}
            </p>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                <div>
                  <div className="text-base font-black" style={{ color: STATUS.critical.hex }}>{topStats.escalations}</div>
                  <div className="text-[9px] text-slate-500">{t("Escalated")}</div>
                </div>
                <div>
                  <div className="text-base font-black" style={{ color: STATUS.warning.hex }}>{topStats.openCases}</div>
                  <div className="text-[9px] text-slate-500">{t("Open")}</div>
                </div>
                <div>
                  <div className="text-base font-black" style={{ color: STATUS.good.hex }}>{topStats.closedCases}</div>
                  <div className="text-[9px] text-slate-500">{t("Resolved")}</div>
                </div>
                <div>
                  <div className="text-base font-black" style={{ color: STATUS.info.hex }}>{topStats.totalAlerts}</div>
                  <div className="text-[9px] text-slate-500">{t("Total")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CROWD TREND + SEVERITY PIE ── */}
      <section>
        <SectionHeader icon="Users" title="Crowd Intelligence Overview"
          subtitle="Actual vs. predicted crowd flow · Risk index · Simhastha peak analysis" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <ChartCard title="Crowd Flow — Actual vs Predicted"
              subtitle="Hourly pilgrim count · AI prediction band · Risk score overlay" live>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={crowdTrendData} margin={{ top:5, right:10, left:-10, bottom:0 }}>
                  <defs>
                    <linearGradient id="actGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={chartColor(0)} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={chartColor(0)} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="predGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={chartColor(6)} stopOpacity={0.15} />
                      <stop offset="100%" stopColor={chartColor(6)} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="time" tick={axisTick} />
                  <YAxis yAxisId="left" tickFormatter={v => `${(v/1000).toFixed(0)}K`} tick={axisTick} />
                  <YAxis yAxisId="right" orientation="right" domain={[0,10]} tickFormatter={v=>`${v}`} tick={axisTick} />
                  <Tooltip content={<ChartTooltip formatter={(v, n) => [n === "Risk Index" ? `${v} / 10` : v?.toLocaleString(), n]} />} />
                  <Legend wrapperStyle={legendStyle} formatter={(value) => t(value)} />
                  <Area yAxisId="left" type="monotone" dataKey="actual"    fill="url(#actGrad)"  stroke={chartColor(0)} strokeWidth={2.5} name="Actual" dot={false} />
                  <Area yAxisId="left" type="monotone" dataKey="predicted" fill="url(#predGrad)" stroke={chartColor(6)} strokeWidth={2} strokeDasharray="5 3" name="Predicted" dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="risk"     stroke={statusHex("critical")} strokeWidth={2} dot={false} name="Risk Index" />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <div className="space-y-4">
            <ChartCard title="Alert Severity Distribution" subtitle="Current operational status">
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={alertSeverityBreakdown} dataKey="value" cx="50%" cy="50%"
                    outerRadius={65} innerRadius={38}
                    label={({ value }) => `${value}`} labelLine={false}>
                    {alertSeverityBreakdown.map((e, i) => (
                      <Cell key={i} fill={chartColor(i)} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-1">
                {alertSeverityBreakdown.map((s, i) => (
                  <div key={s.name} className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background:chartColor(i) }} />
                    <span className="text-[10px] text-slate-600 truncate">{t(s.name)}</span>
                  </div>
                ))}
              </div>
            </ChartCard>

            <ChartCard title="AI Assurance Score">
              <div className="space-y-3">
                {[
                  { label:"Overall Accuracy",     val:aiAssuranceData.accuracy },
                  { label:"Human Validated",      val:aiAssuranceData.humanValidated },
                  { label:"False Positive Control",val:100 - aiAssuranceData.falsePositives },
                  { label:"Alert Confidence",     val:aiAssuranceData.confidence },
                ].map((m, i) => (
                  <div key={m.label}>
                    <div className="flex justify-between text-[10px] text-slate-600 mb-1 font-semibold">
                      <span>{t(m.label)}</span><span>{m.val.toFixed(1)}%</span>
                    </div>
                    <div className="ai-bar-bg">
                      <motion.div className="ai-bar-fill" style={{ background:chartColor(i) }}
                        initial={{ width:0 }} animate={{ width:`${m.val}%` }}
                        transition={{ duration:0.8, delay:0.2 }} />
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/ai-assurance" className="mt-3 flex items-center gap-1 text-[10px] font-bold text-blue-700 hover:text-blue-800">
                {t("View full AI assurance report")} <ChevronRight size={11} />
              </Link>
            </ChartCard>
          </div>
        </div>
      </section>

      {/* ── MULTI-AGENCY STATUS ── */}
      <section>
        <SectionHeader icon="Building2" title="Multi-Agency Operational Status"
          subtitle="Real-time deployment & readiness across 6 government departments" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {departmentStatus.map((dept, i) => (
            <DepartmentCard key={dept.name} dept={dept} index={i} />
          ))}
        </div>
      </section>

      {/* ── ZONE RISK MATRIX + RESOURCES ── */}
      <section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          <ChartCard title="Route Saturation by Corridor" subtitle="Route saturation & crowd density by zone">
            <div className="space-y-3">
              {routeSaturation.map((r, i) => {
                const bucket = loadBucket(r.status);
                return (
                  <motion.div key={r.route}
                    initial={{ opacity:0, x:-12 }}
                    animate={{ opacity:1, x:0 }}
                    transition={{ delay: i * 0.06 }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-slate-700 truncate flex-1">{t(r.route)}</span>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className="text-xs font-black text-slate-900">{r.load}%</span>
                        <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${statusClasses(bucket)}`}>{t(r.status).toUpperCase()}</span>
                      </div>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <motion.div
                        className="h-2 rounded-full"
                        initial={{ width:0 }}
                        animate={{ width:`${r.load}%` }}
                        transition={{ duration:0.8, delay: i * 0.06 }}
                        style={{ background: statusHex(bucket) }}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </ChartCard>

          <ChartCard title="Emergency Resource Deployment" subtitle="Units deployed, available and responding">
            <div className="space-y-3">
              {emergencyUnits.map((unit, i) => (
                <motion.div key={unit.type}
                  initial={{ opacity:0, x:12 }}
                  animate={{ opacity:1, x:0 }}
                  transition={{ delay: i * 0.06 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background:unit.color }} />
                  <span className="text-xs font-semibold text-slate-700 w-20">{t(unit.type)}</span>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-2 rounded-full" style={{
                      width:`${Math.round((unit.deployed/(unit.deployed+unit.available))*100)}%`,
                      background:unit.color
                    }} />
                  </div>
                  <div className="flex gap-2 flex-shrink-0 text-[10px] text-slate-500">
                    <span className="font-bold text-slate-800">{unit.deployed}</span>
                    <span>{t("dep")}</span>
                    <span className="font-semibold" style={{ color: STATUS.good.hex }}>{unit.available} {t("avail")}</span>
                    {unit.responding > 0 && <span className="font-bold" style={{ color: STATUS.critical.hex }}>{unit.responding} {t("live")}</span>}
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 pt-4 border-t border-slate-100">
              <div className="text-center">
                <div className="text-lg font-black" style={{ color: STATUS.info.hex }}>{emergencyUnits.reduce((a,u)=>a+u.deployed,0).toLocaleString()}</div>
                <div className="text-[10px] text-slate-500">{t("Total Deployed")}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-black" style={{ color: STATUS.good.hex }}>{emergencyUnits.reduce((a,u)=>a+u.available,0).toLocaleString()}</div>
                <div className="text-[10px] text-slate-500">{t("Available")}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-black" style={{ color: STATUS.critical.hex }}>{emergencyUnits.reduce((a,u)=>a+u.responding,0)}</div>
                <div className="text-[10px] text-slate-500">{t("Responding")}</div>
              </div>
            </div>
          </ChartCard>
        </div>
      </section>

      {/* ── SERVICE STRESS OVERVIEW ── */}
      <section>
        <SectionHeader icon="Layers" title="Service Stress Intelligence"
          subtitle="Predicted overload across water, toilets, shelters, food, medical, bus, waste · NMC operational view" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <GaugeCard title="Shelter Load"   value={93} index={0} />
          <GaugeCard title="Toilet Usage"  value={89} index={1} />
          <GaugeCard title="Food Zones"    value={82} index={2} />
          <GaugeCard title="Medical Camps" value={61} index={3} />
        </div>
        <div className="mt-4">
          <ChartCard title="Service Utilisation — All Zones"
            subtitle="Current load percentage per service category">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={serviceStress} margin={{ top:5, right:10, left:-10, bottom:0 }}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="name" tick={axisTick} tickFormatter={t} />
                <YAxis tick={axisTick} unit="%" domain={[0,100]} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="used" radius={[4,4,0,0]} name="Load %" label={{ position:"top", fontSize:9, fill:"#64748B" }}>
                  {serviceStress.map((s, i) => (
                    <Cell key={i} fill={statusHex(loadBucket(s.status))} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </section>

      {/* ── COMMAND TIMELINE ── */}
      <section>
        <SectionHeader icon="Clock" title="Real-Time Command Timeline"
          subtitle="Live operational event stream — all departments · escalations · AI actions · resolutions" />
        <div className="card p-4 md:p-6">
          <div className="space-y-4">
            {commandTimeline.map((event, i) => {
              const style = timelineIconStyle[event.type] || { bg:"bg-slate-100", text:"text-slate-600", Icon:Radio };
              const Icon = style.Icon;
              return (
                <motion.div key={i}
                  initial={{ opacity:0, x:-8 }}
                  animate={{ opacity:1, x:0 }}
                  transition={{ delay: i * 0.04 }}
                  className="flex items-start gap-4"
                >
                  {/* Timeline line */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-[10px] font-black ${style.bg} ${style.text}`}>
                      <Icon size={13} />
                    </div>
                    {i < commandTimeline.length - 1 && (
                      <div className="w-px flex-1 min-h-[20px] bg-slate-200 mt-1" />
                    )}
                  </div>

                  <div className="flex-1 pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-800 leading-snug flex-1">{t(event.msg)}</p>
                      <div className="flex items-center gap-2 flex-shrink-0 text-right">
                        <span className="text-[9px] font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{t(event.dept)}</span>
                        <span className="text-xs text-slate-400 font-medium">{event.time}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── PLATFORM POSITIONING ── */}
      <HeroPanel>
        <div className="text-center mb-6">
          <h2 className="text-xl font-black mb-2 text-slate-900">{t("The Maha Kumbh AI Difference")}</h2>
          <p className="text-slate-500 text-sm">
            {t('"Existing Kumbh AI Stack senses events. Maha Kumbh AI validates, predicts, orchestrates response and creates trusted, auditable governance for Simhastha-scale operations."')}
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { icon:Brain, title:"AI Validation", desc:"Every AI alert reviewed before action" },
            { icon:Users, title:"Crowd Prediction", desc:"4-hour ahead crowd risk forecasting" },
            { icon:Shield, title:"Privacy First", desc:"DPDP 2023 · no autonomous enforcement" },
            { icon:BarChart2, title:"Audit Trail", desc:"Complete governance log for all actions" },
          ].map(({ icon:Icon, title, desc }, i) => {
            const c = googleColor(i);
            return (
              <div key={title} className="rounded-2xl p-4 text-center shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                style={{ background:c.bgLight, border:`1px solid ${c.border}` }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-2"
                  style={{ background:c.bg }}>
                  <Icon size={18} style={{ color:c.text }} />
                </div>
                <div className="text-sm font-black mb-1" style={{ color:c.text }}>{t(title)}</div>
                <div className="text-[11px] leading-relaxed" style={{ color:c.text }}>{t(desc)}</div>
              </div>
            );
          })}
        </div>
        <div className="text-center text-sm font-black text-slate-500">
          {t('"AI for Public Safety. AI for Trust. AI for Governance."')}
        </div>
      </HeroPanel>

    </div>
  );
}
